/**
 * MODULE: _shared/frontend/lib/frontend/utils/salesTargetSetBridge.ts
 * PURPOSE: Open generative quota-set modal from assistant, My quota, and onboarding.
 */

import { getApiBaseUrl, getAuthHeaders } from '../api/config';
import { requestAssistantBrainContextRefresh } from './emailTemplateCreateBridge';

export type SalesTargetSetPrefill = {
  targetAmount?: number;
  fiscalYear?: number;
  fiscalQuarter?: number;
  territoryId?: string;
};

export type SalesTargetSetOverlayContext = {
  modal: 'sales_target_set';
  mode: 'create' | 'edit';
  periodLabel?: string;
  targetAmount?: number;
  fiscalYear?: number;
  fiscalQuarter?: number;
};

export type SalesTargetSetOpenOptions = {
  prefill?: SalesTargetSetPrefill;
  mode?: 'create' | 'edit';
  coachInAssistant?: boolean;
};

export const OPEN_SALES_TARGET_SET_EVENT = 'brazn:open-sales-target-set';
export const SALES_TARGET_SET_OVERLAY_EVENT = 'brazn:sales-target-set-overlay';

let overlaySnapshot: SalesTargetSetOverlayContext | null = null;

export function getSalesTargetSetOverlayContext(): SalesTargetSetOverlayContext | null {
  return overlaySnapshot;
}

export function notifySalesTargetSetOverlay(context: SalesTargetSetOverlayContext | null): void {
  overlaySnapshot = context;
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<SalesTargetSetOverlayContext | null>(SALES_TARGET_SET_OVERLAY_EVENT, {
      detail: context,
    })
  );
}

export function subscribeSalesTargetSetOverlay(
  listener: (context: SalesTargetSetOverlayContext | null) => void
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const handler = (event: Event) => {
    listener((event as CustomEvent<SalesTargetSetOverlayContext | null>).detail ?? null);
  };
  window.addEventListener(SALES_TARGET_SET_OVERLAY_EVENT, handler);
  return () => window.removeEventListener(SALES_TARGET_SET_OVERLAY_EVENT, handler);
}

export function requestOpenSalesTargetSet(options?: SalesTargetSetOpenOptions): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<SalesTargetSetOpenOptions>(OPEN_SALES_TARGET_SET_EVENT, {
      detail: options ?? {},
    })
  );
}

export function subscribeOpenSalesTargetSet(
  listener: (options: SalesTargetSetOpenOptions) => void
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const handler = (event: Event) => {
    listener((event as CustomEvent<SalesTargetSetOpenOptions>).detail ?? {});
  };
  window.addEventListener(OPEN_SALES_TARGET_SET_EVENT, handler);
  return () => window.removeEventListener(OPEN_SALES_TARGET_SET_EVENT, handler);
}

/** Rebuild world model + refresh assistant brain chips after quota mutations. */
export function triggerQuotaPlanningLearningRefresh(): void {
  const base = getApiBaseUrl().replace(/\/$/, '');
  void fetch(`${base}/api/v1/assistant/world-model/refresh?force=true`, {
    method: 'POST',
    headers: getAuthHeaders(),
  }).catch(() => undefined);
  requestAssistantBrainContextRefresh();
}
